import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Download, Printer, Wifi, Server, CheckCircle, AlertCircle,
  Monitor, Settings, FileDown, Sparkles, Zap, Shield, Clock,
  ArrowLeft, Image as ImageIcon, Cpu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../components/LanguageContext';

const EXE_PATH = '/downloads/PhotoBoothPrintServer.exe';
const EXE_SIZE_MB = '~42';

export default function DownloadPrintServer() {
  const { language, isRTL } = useLanguage();
  const isHe = language === 'he';
  const [exeAvailable, setExeAvailable] = useState(null);
  const [fileSize, setFileSize] = useState(null);

  useEffect(() => {
    fetch(EXE_PATH, { method: 'HEAD' })
      .then((r) => {
        setExeAvailable(r.ok);
        const sizeHeader = r.headers.get('content-length');
        const sizeBytes = sizeHeader ? parseInt(sizeHeader, 10) : 0;
        // Only use the header if it's actually > 0 (some dev servers return 0 for HEAD)
        if (sizeBytes > 1024) {
          setFileSize((sizeBytes / (1024 * 1024)).toFixed(1));
        }
      })
      .catch(() => setExeAvailable(false));
  }, []);

  const steps = isHe ? [
    { num: 1, icon: Download,  title: 'הורד את התוכנה',   description: 'לחץ על כפתור ההורדה למטה. הקובץ PhotoBoothPrintServer.exe יישמר במחשב המחובר למדפסת.' },
    { num: 2, icon: Settings,  title: 'בחר קוד חיבור',    description: 'צור קובץ .env ליד התוכנה ובתוכו שורה אחת: PRINT_RELAY_CODE=הקוד-שלך (לפחות 6 תווים, אותיות וספרות).' },
    { num: 3, icon: Monitor,   title: 'הפעל בקליק כפול',  description: 'פתח את הקובץ. ייפתח חלון שמציג את קוד החיבור ומאשר "Relay connected". השאר אותו פתוח.' },
    { num: 4, icon: Wifi,      title: 'הזן את הקוד באפליקציה', description: 'באפליקציה, בלחיצה על "הדפס", יפתח דיאלוג. הזן בו את אותו קוד חיבור. אין צורך באותה רשת WiFi.' },
    { num: 5, icon: Printer,   title: 'הדפס!',            description: 'בחר את המדפסת, את גודל הנייר (4×6 / 6×8) ואת מספר העותקים. לחץ הדפס. זהו.' },
  ] : [
    { num: 1, icon: Download,  title: 'Download the software', description: 'Click the download button below. PhotoBoothPrintServer.exe will save to the PC connected to your printer.' },
    { num: 2, icon: Settings,  title: 'Choose a pairing code', description: 'Create a .env file next to the program containing one line: PRINT_RELAY_CODE=your-code (at least 6 letters/digits).' },
    { num: 3, icon: Monitor,   title: 'Double-click to launch', description: 'Open the file. A window appears showing the pairing code and "Relay connected". Keep it open while printing.' },
    { num: 4, icon: Wifi,      title: 'Enter the code in the app', description: 'In the app, press "Print" — a dialog opens. Enter the same pairing code. The devices do not need to share a WiFi network.' },
    { num: 5, icon: Printer,   title: 'Print!',                description: 'Pick your printer, paper size (4×6 / 6×8), and number of copies. Hit Print. Done.' },
  ];

  const features = isHe ? [
    { icon: Zap,        title: 'ללא התקנה',       description: 'קליק כפול והתוכנה רצה. אין installer, אין דרישות מקדימות.' },
    { icon: Shield,     title: '100% מקומי',       description: 'התמונות לא יוצאות מהרשת המקומית שלך. הכל בין הטאבלט למדפסת.' },
    { icon: Clock,      title: 'מהיר מאוד',        description: 'תמונה מגיעה למדפסת תוך פחות משנייה. רק זמן ההדפסה הפיזית.' },
    { icon: Cpu,        title: 'קל על המחשב',      description: '42MB בלבד. רץ ברקע ולא צורך משאבים כשלא בשימוש.' },
  ] : [
    { icon: Zap,        title: 'No installation',  description: 'Double-click and run. No installer, no prerequisites.' },
    { icon: Shield,     title: '100% local',       description: 'Photos never leave your local network. Direct tablet → printer.' },
    { icon: Clock,      title: 'Very fast',        description: 'Image reaches printer in under a second. Only physical print time remains.' },
    { icon: Cpu,        title: 'Lightweight',      description: 'Only 42MB. Runs quietly in the background.' },
  ];

  const printerSupport = [
    { brand: 'DNP',        models: 'DS620, DS820, RX1, QW410',         featured: true },
    { brand: 'Mitsubishi', models: 'CP-D90DW, CP-K60DW',                featured: true },
    { brand: 'Canon',      models: 'Selphy CP1500, CP1300',             featured: false },
    { brand: 'HiTi',       models: 'P525L, P710L',                       featured: false },
    { brand: isHe ? 'כל מדפסת Windows' : 'Any Windows printer',
      models: isHe ? 'כל מדפסת מותקנת ב-Windows' : 'Anything installed in Windows works',
      featured: false },
  ];

  const paperSizes = ['4×6', '5×7', '6×8', '6×9', 'A4', 'Letter'];

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 text-sm"
        >
          <ArrowLeft className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
          {isHe ? 'חזרה לדף הבית' : 'Back to home'}
        </Link>

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
            <Sparkles className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-medium text-violet-400">
              {isHe ? 'תוכנה למחשב — הדפסה אלחוטית' : 'Desktop software — wireless printing'}
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {isHe ? 'שרת הדפסה' : 'Print Server'}
            <span className="block bg-gradient-to-r from-violet-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
              PhotoBoothPrintServer
            </span>
          </h1>

          <p className="text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-8">
            {isHe
              ? 'תוכנה אחת קטנה למחשב שמחובר למדפסת הצילום שלך. אפליקציית AI PhotoBooth שולחת אליה את התמונות דרך WiFi והמדפסת מדפיסה אוטומטית.'
              : 'A small Windows app for the PC connected to your photo printer. The AI PhotoBooth tablet sends images over WiFi and they print automatically.'}
          </p>
        </motion.div>

        {/* Download CTA Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="glass-card rounded-3xl p-8 md:p-12 mb-20 bg-gradient-to-br from-violet-600/10 to-pink-600/10 border-violet-500/20"
        >
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Icon side */}
            <div className="shrink-0">
              <div className="w-32 h-32 rounded-3xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center shadow-lg shadow-violet-600/40">
                <FileDown className="w-16 h-16 text-white" />
              </div>
            </div>

            {/* Content side */}
            <div className="flex-1 text-center md:text-start">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                PhotoBoothPrintServer.exe
              </h2>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm text-slate-400 mb-6">
                <span className="inline-flex items-center gap-1">
                  <Monitor className="w-4 h-4" /> Windows 10 / 11
                </span>
                <span>·</span>
                <span>{fileSize ? `${fileSize} MB` : `${EXE_SIZE_MB} MB`}</span>
                <span>·</span>
                <span>{isHe ? 'ללא התקנה' : 'No install required'}</span>
              </div>

              {exeAvailable === true && (
                <a href={EXE_PATH} download>
                  <Button className="premium-button h-14 px-8 text-base font-semibold gap-2 rounded-xl">
                    <Download className="w-5 h-5" />
                    {isHe ? 'הורד את התוכנה' : 'Download for Windows'}
                  </Button>
                </a>
              )}

              {exeAvailable === false && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4 text-start text-sm text-amber-200 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">
                      {isHe ? 'התוכנה עדיין לא הועלתה לשרת.' : 'The EXE has not been uploaded to the server yet.'}
                    </p>
                    <p className="text-xs">
                      {isHe
                        ? 'מנהל המערכת צריך להריץ local-api/build-exe.bat ולהעלות לתיקיית public/downloads/.'
                        : 'Admin: run local-api/build-exe.bat, then upload to public/downloads/.'}
                    </p>
                  </div>
                </div>
              )}

              {exeAvailable === null && (
                <p className="text-xs text-slate-500">{isHe ? 'בודק זמינות...' : 'Checking availability...'}</p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white text-center mb-12"
          >
            {isHe ? 'איך מתחילים — 5 שלבים' : 'Setup in 5 Steps'}
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className="relative"
              >
                <div className="glass-card rounded-2xl p-6 h-full">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center mb-4">
                    <step.icon className="w-7 h-7 text-white" />
                  </div>
                  <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center`}>
                    <span className="text-sm font-bold text-violet-400">{step.num}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            {isHe ? 'למה דווקא התוכנה הזאת?' : 'Why use this app?'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="glass-card rounded-xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600/20 to-pink-600/20 flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-6 h-6 text-violet-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Supported printers */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-24"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-3">
            {isHe ? 'מדפסות נתמכות' : 'Supported Printers'}
          </h2>
          <p className="text-slate-400 text-center mb-12">
            {isHe ? 'כל המדפסות המקצועיות שמשמשות בעמדות צילום, ובנוסף כל מדפסת Windows' : 'All major photo booth printers, plus any Windows printer'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {printerSupport.map((p, i) => (
              <div
                key={i}
                className={`glass-card rounded-xl p-5 flex items-start gap-3 ${
                  p.featured ? 'border-violet-500/40' : ''
                }`}
              >
                <CheckCircle className={`w-5 h-5 shrink-0 mt-0.5 ${p.featured ? 'text-violet-400' : 'text-green-400'}`} />
                <div>
                  <p className="text-white font-semibold mb-0.5">{p.brand}</p>
                  <p className="text-xs text-slate-400">{p.models}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Paper sizes */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <ImageIcon className="w-5 h-5 text-violet-400" />
              <h3 className="text-white font-semibold">
                {isHe ? 'גודלי נייר נתמכים' : 'Supported paper sizes'}
              </h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {paperSizes.map((size) => (
                <span
                  key={size}
                  className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-sm text-slate-300 font-mono"
                >
                  {size}
                </span>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Firewall warning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card rounded-2xl p-6 mb-12 border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold mb-2">
                {isHe ? 'הערה לגבי חומת אש (Windows Firewall)' : 'Windows Firewall note'}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {isHe
                  ? 'בפעם הראשונה שתפעיל את התוכנה, Windows יציג חלון אישור — לחץ "Allow access" עבור Private networks. בלי זה הטאבלט לא יוכל להתחבר אל המחשב.'
                  : 'The first time you run the app, Windows will show a permission prompt — click "Allow access" for Private networks. Without it, the tablet won\'t be able to connect to the PC.'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Technical accordion */}
        <details className="glass-card rounded-2xl p-6 mb-12 group">
          <summary className="cursor-pointer text-white font-semibold flex items-center gap-3 list-none">
            <Server className="w-5 h-5 text-violet-400" />
            <span>{isHe ? 'מה התוכנה הזאת עושה בדיוק? (טכני)' : 'What does this actually do? (technical)'}</span>
            <span className="ms-auto text-slate-500 group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="mt-4 pt-4 border-t border-slate-700 text-sm text-slate-400 space-y-3 leading-relaxed">
            <p>{isHe
              ? 'התוכנה פותחת חיבור WebSocket יוצא אל photo.b-magnet.co.il ומחכה שם לקוד החיבור שלך. כשהאפליקציה רוצה להדפיס, היא שולחת את התמונה כ-base64 דרך אותו חיבור. התוכנה שומרת את התמונה לקובץ זמני, פותחת את Windows Printing API, מגדירה את גודל הנייר והעותקים שביקשת, ושולחת להדפסה למדפסת שבחרת.'
              : 'The program opens an outbound WebSocket to photo.b-magnet.co.il and waits there under your pairing code. When the app wants to print, it sends the image as base64 over that connection. The program saves it as a temp file, opens the Windows Printing API, sets the paper size and copies you chose, and sends the job to the selected printer.'}
            </p>
            <p>{isHe
              ? 'למה דרך השרת ולא ישירות? האתר רץ ב-HTTPS, והדפדפן חוסם חיבור ws:// לא-מוצפן מדף מאובטח — לכן חיבור ישיר לכתובת IP מקומית לא יכול לעבוד. דרך אגב, ככה גם אפשר להדפיס כשהטלפון על 4G ולא על אותה רשת WiFi.'
              : 'Why through the server and not directly? The site runs on HTTPS, and browsers block an insecure ws:// connection from a secure page — so dialling a LAN IP could never work. As a bonus, printing now also works when the phone is on 4G rather than the same WiFi.'}
            </p>
            <p>{isHe
              ? 'התוכנה עדיין מאזינה גם מקומית על פורט 8765 (שימושי בהתקנות קיוסק ב-HTTP), ואין דרישות התקנה — הקובץ EXE מכיל את כל Node.js בתוכו.'
              : 'It still listens locally on port 8765 too (useful for HTTP kiosk setups), and needs no install — the EXE bundles Node.js inside it.'}
            </p>
          </div>
        </details>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card rounded-3xl p-12 text-center bg-gradient-to-br from-violet-600/10 to-pink-600/10 border-violet-500/20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {isHe ? 'מוכן להתחיל להדפיס?' : 'Ready to start printing?'}
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            {isHe
              ? 'הורד את התוכנה, הפעל אותה במחשב עם המדפסת, וצור תמונות מודפסות מאירועים בשניות.'
              : 'Download the app, run it on the PC with your printer, and create printed event photos in seconds.'}
          </p>
          {exeAvailable && (
            <a href={EXE_PATH} download>
              <Button className="premium-button h-14 px-8 text-base font-semibold gap-2 rounded-xl">
                <Download className="w-5 h-5" />
                {isHe ? `הורד עכשיו · ${fileSize || EXE_SIZE_MB} MB` : `Download now · ${fileSize || EXE_SIZE_MB} MB`}
              </Button>
            </a>
          )}
        </motion.div>
      </div>
    </div>
  );
}
